import { createActionHandler, createReducer, OperationStatus } from '@pressreader/appstore';
import { userChangeDone } from '@pressreader/authentication-core';
import type { ArticleCollections, IArticleSharePreview, ShareProvider } from '@pressreader/social-types';

import {
    loadBookmarksForArticleBegan,
    loadBookmarksForArticleCancelled,
    loadBookmarksForArticleDone,
    loadBookmarksForArticleFailed,
    loadShareInfoForArticleDone,
    resetBookmarksAction,
} from '../actions/share';

interface State {
    bookmarks: Record<string, { status: OperationStatus; bookmarks: ArticleCollections[] }>;
    providers: {
        [articleId: string]: { status: OperationStatus; supported: ShareProvider[] };
    };
    previews: {
        [articleId: string]: IArticleSharePreview;
    };
}

const initialState: State = {
    bookmarks: {},
    providers: {},
    previews: {},
};

function getArticleBookmarksState(state: State, articleId: string) {
    if (articleId in state.bookmarks) {
        return state.bookmarks[articleId];
    }

    return { status: OperationStatus.None, bookmarks: [] };
}

/**
 * Moves a `Pending` bookmarks load to `status`; an entry that is not `Pending` has already settled or been reset and is
 * left as it is.
 */
function settlePendingBookmarks(state: State, articleId: string, status: OperationStatus): State {
    const articleBookmarksState = getArticleBookmarksState(state, articleId);
    if (articleBookmarksState.status !== OperationStatus.Pending) {
        return state;
    }

    return {
        ...state,
        bookmarks: {
            ...state.bookmarks,
            [articleId]: { ...articleBookmarksState, status },
        },
    };
}

const reducer = createReducer(
    initialState,
    createActionHandler(loadBookmarksForArticleBegan, (state, { payload }) => {
        const articleBookmarksState = {
            ...getArticleBookmarksState(state, payload.articleId),
            status: OperationStatus.Pending,
        };

        return {
            ...state,
            bookmarks: {
                ...state.bookmarks,
                [payload.articleId]: articleBookmarksState,
            },
        };
    }),
    createActionHandler(loadBookmarksForArticleDone, (state, { payload }) => {
        const articleBookmarksState = {
            ...getArticleBookmarksState(state, payload.articleId),
            status: OperationStatus.Done,
            bookmarks: payload.bookmarks,
        };

        return {
            ...state,
            bookmarks: {
                ...state.bookmarks,
                [payload.articleId]: articleBookmarksState,
            },
        };
    }),
    createActionHandler(loadBookmarksForArticleFailed, (state, { payload }) =>
        settlePendingBookmarks(state, payload.articleId, OperationStatus.Failed),
    ),
    createActionHandler(loadBookmarksForArticleCancelled, (state, { payload }) =>
        settlePendingBookmarks(state, payload.articleId, OperationStatus.None),
    ),
    createActionHandler(userChangeDone, state => ({
        ...state,
        previews: {},
        providers: {},
    })),
    createActionHandler(loadShareInfoForArticleDone, (state, { payload }) => {
        const { articleId } = payload;
        return {
            ...state,
            previews: {
                ...state.previews,
                [articleId]: payload.preview,
            },
            providers: {
                ...state.providers,
                [articleId]: {
                    supported: payload.supported,
                    status: OperationStatus.Done,
                },
            },
        };
    }),
    createActionHandler(resetBookmarksAction, state => ({
        ...state,
        bookmarks: {},
    })),
);

export { reducer, State };
